import React, {Component} from 'react';
import PropTypes from 'prop-types';
import Random from 'random-id';
import {CustomStep, OptionsStep, TextStep} from './steps_components';
import schema from './schemas/schema';
import * as storage from './storage';
import {
    ChatBotContainer,
    Content,
    Header,
    HeaderTitle,
    HeaderIcon,
    FloatButton,
    FloatingIcon,
    Footer,
    Input,
    SubmitButton,
} from './components';
import Recognition from './recognition';
import {
    ChatIcon,
    CloseIcon,
    SubmitIcon,
    MicIcon,
} from './icons';
import {isMobile} from './utils';
import {speakFn} from './speechSynthesis';
import HeaderButton from "./components/HeaderButton";

class ChatBot extends Component {
    /* istanbul ignore next */
    constructor(props) {
        super(props);

        this.state = {
            store: {
                patient: {
                    id: null,
                    name: null,
                    email: null,
                },
                settings: {
                    language: 'en',
                    validationTexts: [],
                    texts: [],
                },
                generalInfo:{},
            },
            isRTL: false,
            renderedSteps: [],
            previousSteps: [],
            currentStep: {},
            previousStep: {},
            steps: {},
            disabled: true,
            opened: props.opened || !props.floating,
            inputValue: '',
            inputInvalid: false,
            speaking: false,
            recognitionEnable: props.recognitionEnable && Recognition.isSupported(),
            defaultUserSettings: {},
        };

        this.speak = speakFn(props.speechSynthesis);
    }

    componentWillMount() {
        const {steps} = this.props;
        const {
            botDelay,
            botAvatar,
            cache,
            cacheName,
            settings,
            customDelay,
            enableMobileAutoFocus,
            userAvatar,
            userDelay,
        } = this.props;
        const chatSteps = {};

        const defaultBotSettings = {delay: botDelay, avatar: botAvatar};
        const defaultUserSettings = {delay: userDelay, avatar: userAvatar};
        const defaultCustomSettings = {delay: customDelay};


        this.updateStore('settings', settings);

        for (let i = 0, len = steps.length; i < len; i += 1) {
            const step = steps[i];
            let settings = {};

            if (step.user) {
                settings = defaultUserSettings;
            } else if (step.message || step.asMessage) {
                settings = defaultBotSettings;
            } else if (step.component) {
                settings = defaultCustomSettings;
            }

            chatSteps[step.id] = Object.assign({}, settings, schema.parse(step));
        }

        schema.checkInvalidIds(chatSteps);

        const firstStep = steps[0];

        if (firstStep.message) {
            const {message} = firstStep;
            firstStep.message = typeof message === 'function' ? message() : message;
            chatSteps[firstStep.id].message = firstStep.message;
        }

        const {
            currentStep,
            previousStep,
            previousSteps,
            renderedSteps,
        } = storage.getData(
            {
                cacheName,
                cache,
                firstStep,
                steps: chatSteps,
            },
            () => {
                // focus input if last step cached is a user step
                this.setState({disabled: false}, () => {
                    if (enableMobileAutoFocus || !isMobile()) {
                        this.input.focus();
                    }
                });
            },
        );

        this.setState({
            currentStep,
            defaultUserSettings,
            previousStep,
            previousSteps,
            renderedSteps,
            isRTL: this.isRtlSite(),
            steps: chatSteps,
        });
    }

    componentDidMount() {
        const {recognitionEnable} = this.state;
        const {recognitionLang} = this.props;

        if (recognitionEnable) {
            this.recognition = new Recognition(
                this.onRecognitionChange,
                this.onRecognitionEnd,
                this.onRecognitionStop,
                recognitionLang,
            );
        }

        if (this.content) {
            this.content.addEventListener('DOMNodeInserted', this.onNodeInserted);
            window.addEventListener('resize', this.onResize);
            document.addEventListener('click', this.handleClickOutside);
        }
    }

    componentWillUpdate(nextProps, nextState) {
        const {opened} = nextProps;

        if (opened !== undefined && opened !== nextState.opened) {
            this.setState({opened});
        }
    }

    componentWillUnmount() {
        if (this.content) {
            this.content.removeEventListener('DOMNodeInserted', this.onNodeInserted);
            window.removeEventListener('resize', this.onResize);
            document.removeEventListener('click', this.handleClickOutside);
        }
    }

    handleClickOutside = (event) => {
        if (event.target.classList.contains('toggleChatBot')) {
            event.preventDefault();
            this.toggleChatBot(!this.state.opened);
            return false;
        }
    }

    onNodeInserted = (event) => {
        event.currentTarget.scrollTop = event.currentTarget.scrollHeight;
    }

    onResize = () => {
        this.content.scrollTop = this.content.scrollHeight;
    }

    onRecognitionChange = (value) => {
        this.setState({inputValue: value});
    }

    onRecognitionEnd = () => {
        this.setState({speaking: false});
        this.handleSubmitButton();
    }

    onRecognitionStop = () => {
        this.setState({speaking: false});
    }

    onValueChange = (event) => {
        this.setState({inputValue: event.target.value});
    }

    getTriggeredStep = (trigger, value) => {
        const steps = this.generateRenderedStepsById();
        return typeof trigger === 'function' ? trigger({value, steps}) : trigger;
    }

    getStepMessage = (message) => {
        const {previousSteps} = this.state;
        const lastStepIndex = previousSteps.length > 0 ? previousSteps.length - 1 : 0;
        const steps = this.generateRenderedStepsById();
        const previousValue = previousSteps[lastStepIndex].value;
        return typeof message === 'function' ? message({previousValue, steps}) : message;
    }

    generateRenderedStepsById = () => {
        const {previousSteps} = this.state;
        const steps = {};

        for (let i = 0, len = previousSteps.length; i < len; i += 1) {
            const {
                id,
                message,
                value,
                metadata,
            } = previousSteps[i];

            steps[id] = {
                id,
                message,
                value,
                metadata,
            };
        }

        return steps;
    };

    isRtlSite = () => {
        return (document.dir == 'rtl') ? true : false;
    };

    startAgain = () => {
        let firstStep = this.state.renderedSteps[0];
        this.setState(
            {
                previousSteps: [firstStep],
                renderedSteps: [firstStep],
                previousStep: firstStep,
                currentStep: firstStep,
                inputValue: '',
                disabled: true
            }, () => {
                this.triggerNextStep({trigger: firstStep.trigger})
            }
        );
    };

    backOneStep = () => {
        let lastStepIndex = 0;
        const {
            previousSteps,
            renderedSteps,
        } = this.state;
        renderedSteps.pop();
        previousSteps.pop();
        if (previousSteps.length > 2) {
            for (let i = previousSteps.length - 1; i >= 0; i--) {
                if (previousSteps[i].user === true) {
                    renderedSteps.pop();
                    previousSteps.pop();
                    break;
                }
                renderedSteps.pop();
                previousSteps.pop();
            }
        }
        lastStepIndex = renderedSteps.length > 0 ? renderedSteps.length - 1 : 0;

        this.setState({
            previousStep: previousSteps[lastStepIndex],
            currentStep: previousSteps,
            inputValue: '',
            disabled: true,
            renderedSteps,
            previousSteps,
        }, () => {
            this.triggerNextStep({trigger: renderedSteps[lastStepIndex].trigger})
        })

    };

    updateStore = (key, value) => {
        const {store} = this.state;
        const {changeHeaderColorAction} = this.props;

        if (typeof value == 'object') {
            value = Object.assign({}, store[key], value);
        }
        store[key] = value;

        this.setState({store});

        if (key === 'patient'
            && typeof value == 'object'
            && value.verified
        ) {

            changeHeaderColorAction();
        }
    };

    triggerNextStep = (data) => {
        const {enableMobileAutoFocus} = this.props;
        const {
            defaultUserSettings,
            previousSteps,
            renderedSteps,
            steps,
        } = this.state;

        let {currentStep, previousStep} = this.state;
        const isEnd = currentStep.end;

        if (data && data.value) {
            currentStep.value = data.value;
        }
        if (data && data.replace) {
            currentStep.replace = data.replace;
        }
        if (data && data.hideInput) {
            currentStep.hideInput = data.hideInput;
        }
        if (data && data.disable) {
            this.setState({
                inputValue: '',
                disabled: true
            })
        }
        if (data && data.trigger) {
            currentStep.trigger = this.getTriggeredStep(data.trigger, data.value);
        }
        if (data && data.userAnswer) {
            currentStep.userAnswer = data.userAnswer;
        }

        if (isEnd) {
            this.handleEnd();
        } else if (currentStep.userAnswer && data) {
            delete currentStep.component;


            // replace choose component for user message
            currentStep = Object.assign({}, currentStep, defaultUserSettings, {
                user: true,
                message: currentStep.userAnswer,
                trigger: currentStep.trigger,
            });

            renderedSteps.pop();
            previousSteps.pop();
            renderedSteps.push(currentStep);
            previousSteps.push(currentStep);

            this.setState({
                currentStep,
                renderedSteps,
                previousSteps,
            });
        } else if (currentStep.options && data) {
            const option = currentStep.options.filter(o => o.value === data.value)[0];
            const trigger = this.getTriggeredStep(option.trigger, currentStep.value);
            delete currentStep.options;

            // replace choose option for user message
            currentStep = Object.assign({}, currentStep, option, defaultUserSettings, {
                user: true,
                message: option.label,
                trigger,
            });

            renderedSteps.pop();
            previousSteps.pop();
            renderedSteps.push(currentStep);
            previousSteps.push(currentStep);

            this.setState({
                currentStep,
                renderedSteps,
                previousSteps,
            });
        } else if (currentStep.trigger) {
            if (currentStep.replace) {
                renderedSteps.pop();
            }

            const trigger = this.getTriggeredStep(currentStep.trigger, currentStep.value);
            let nextStep = Object.assign({}, steps[trigger]);

            if (nextStep.message) {
                nextStep.message = this.getStepMessage(nextStep.message);
            } else if (nextStep.update) {
                const updateStep = nextStep;
                nextStep = Object.assign({}, steps[updateStep.update]);

                if (nextStep.options) {
                    for (let i = 0, len = nextStep.options.length; i < len; i += 1) {
                        nextStep.options[i].trigger = updateStep.trigger;
                    }
                } else {
                    nextStep.trigger = updateStep.trigger;
                }
            }

            nextStep.key = Random(24);

            previousStep = currentStep;
            currentStep = nextStep;

            this.setState({renderedSteps, currentStep, previousStep}, () => {
                if (nextStep.user) {
                    this.setState({disabled: false}, () => {
                        if (enableMobileAutoFocus || !isMobile()) {
                            this.input.focus();
                        }
                    });
                } else {
                    renderedSteps.push(nextStep);
                    previousSteps.push(nextStep);

                    this.setState({renderedSteps, previousSteps});
                }
            });
        }

        const {cache, cacheName} = this.props;
        if (cache) {
            setTimeout(() => {
                storage.setData(cacheName, {
                    currentStep,
                    previousStep,
                    previousSteps,
                    renderedSteps,
                });
            }, 300);
        }
    }

    handleEnd = () => {
        const {handleEnd} = this.props;

        if (handleEnd) {
            const {previousSteps} = this.state;

            const renderedSteps = previousSteps.map((step) => {
                const {
                    id,
                    message,
                    value,
                    metadata,
                } = step;

                return {
                    id,
                    message,
                    value,
                    metadata,
                };
            });

            const steps = [];

            for (let i = 0, len = previousSteps.length; i < len; i += 1) {
                const {
                    id,
                    message,
                    value,
                    metadata,
                } = previousSteps[i];

                steps[id] = {
                    id,
                    message,
                    value,
                    metadata,
                };
            }

            const values = previousSteps.filter(step => step.value).map(step => step.value);

            handleEnd({renderedSteps, steps, values});
        }
    }

    isInputValueEmpty = () => {
        const {inputValue} = this.state;
        return Boolean(inputValue) && inputValue.length > 0;
    }

    isLastPosition = (step) => {
        const {renderedSteps} = this.state;
        const {length} = renderedSteps;
        const stepIndex = renderedSteps.map(s => s.key).indexOf(step.key);

        if (length <= 1 || stepIndex + 1 === length) {
            return true;
        }

        const nextStep = renderedSteps[stepIndex + 1];
        const hasMessage = nextStep.message || nextStep.asMessage;

        if (!hasMessage) {
            return true;
        }

        const isLast = step.user !== nextStep.user;
        return isLast;
    }

    isFirstPosition = (step) => {
        const {renderedSteps} = this.state;
        const stepIndex = renderedSteps.map(s => s.key).indexOf(step.key);

        if (stepIndex === 0) {
            return true;
        }

        const lastStep = renderedSteps[stepIndex - 1];
        const hasMessage = lastStep.message || lastStep.asMessage;

        if (!hasMessage) {
            return true;
        }

        const isFirst = step.user !== lastStep.user;
        return isFirst;
    }

    handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            this.submitUserMessage();
        }
    }

    handleSubmitButton = () => {
        const {speaking, recognitionEnable} = this.state;
        if ((this.isInputValueEmpty() || speaking) && recognitionEnable) {
            this.recognition.speak();
            if (!speaking) {
                this.setState({speaking: true});
            }
            return;
        }
        this.submitUserMessage();
    }

    submitUserMessage = () => {
        const {
            defaultUserSettings,
            previousSteps,
            renderedSteps,
        } = this.state;

        let {
            currentStep,
            inputValue
        } = this.state;

        if (typeof currentStep.mutation == 'function') {
            inputValue = currentStep.mutation(inputValue);
            this.setState(
                {
                    inputValue: inputValue,
                }
            );
        }

        const isInvalid = currentStep.validator && this.checkInvalidInput();

        if (!isInvalid) {
            const step = {
                message: inputValue,
                value: inputValue,
            };

            currentStep = Object.assign({}, defaultUserSettings, currentStep, step);

            renderedSteps.push(currentStep);
            previousSteps.push(currentStep);

            this.setState(
                {
                    currentStep,
                    renderedSteps,
                    previousSteps,
                    disabled: true,
                    inputValue: '',
                },
                () => {
                    // this.input.blur();
                },
            );
        }
    }

    checkInvalidInput = () => {
        const {enableMobileAutoFocus} = this.props;
        const {currentStep, store} = this.state;
        let {inputValue} = this.state;

        if (typeof currentStep.mutation == 'function') {
            inputValue = currentStep.mutation(inputValue);
        }

        const result = currentStep.validator(inputValue, store.settings.validationTexts, store.settings.language);

        if (typeof result !== 'boolean' || !result) {
            this.setState(
                {
                    inputValue: result.toString(),
                    inputInvalid: true,
                    disabled: true,
                },
                () => {
                    setTimeout(() => {
                        this.setState(
                            {
                                inputValue: inputValue,
                                inputInvalid: false,
                                disabled: false,
                            },
                            () => {
                                if (enableMobileAutoFocus || !isMobile()) {
                                    this.input.focus();
                                }
                            },
                        );
                    }, 2000);
                },
            );

            return true;
        }

        return false;
    }

    toggleChatBot = (opened) => {
        const {toggleFloating} = this.props;

        if (toggleFloating) {
            toggleFloating({opened});
        } else {
            this.setState({opened});
        }
    }

    renderStep = (step, index) => {
        const {renderedSteps, store, isRTL} = this.state;
        const {
            avatarStyle,
            bubbleStyle,
            bubbleOptionStyle,
            customStyle,
            hideBotAvatar,
            hideUserAvatar,
            speechSynthesis,
            botAvatar
        } = this.props;
        const {options, component, asMessage} = step;
        const steps = this.generateRenderedStepsById();
        const allSteps = this.state.steps;
        const previousStep = index > 0 ? renderedSteps[index - 1] : {};

        if (component && !asMessage) {

            return (
                <CustomStep

                    botAvatar={botAvatar}
                    key={index}
                    customOptions={[]}
                    speak={this.speak}
                    step={step}
                    steps={steps}
                    store={store}
                    style={customStyle}
                    allSteps={allSteps}
                    previousStep={previousStep}
                    bubbleStyle={bubbleStyle}
                    previousValue={previousStep.value}
                    bubbleOptionStyle={bubbleOptionStyle}
                    triggerNextStep={this.triggerNextStep}
                    updateStore={this.updateStore}
                    avatarStyle={avatarStyle}
                    isFirst={this.isFirstPosition(step)}
                    isLast={this.isLastPosition(step)}
                    isRTL={isRTL}
                />
            );
        }

        if (options) {
            return (
                <OptionsStep
                    key={index}
                    step={step}
                    speak={this.speak}
                    previousValue={previousStep.value}
                    triggerNextStep={this.triggerNextStep}
                    allSteps={allSteps}
                    bubbleOptionStyle={bubbleOptionStyle}
                />
            );
        }

        return (
            <TextStep
                key={index}
                step={step}
                steps={steps}
                store={store}
                speak={this.speak}
                previousStep={previousStep}
                previousValue={previousStep.value}
                triggerNextStep={this.triggerNextStep}
                updateStore={this.updateStore}
                avatarStyle={avatarStyle}
                bubbleStyle={bubbleStyle}
                allSteps={allSteps}
                hideBotAvatar={hideBotAvatar}
                hideUserAvatar={hideUserAvatar}
                speechSynthesis={speechSynthesis}
                isFirst={this.isFirstPosition(step)}
                isLast={this.isLastPosition(step)}
                isRTL={isRTL}
            />
        );
    }

    render() {
        const {
            currentStep,
            disabled,
            inputInvalid,
            inputValue,
            opened,
            renderedSteps,
            speaking,
            recognitionEnable,
        } = this.state;
        const {
            className,
            contentStyle,
            floating,
            floatingIcon,
            floatingStyle,
            floatingText,
            footerStyle,
            headerComponent,
            prevStepButton,
            startAgainButton,
            hideHeader,
            headerTitle,
            hideSubmitButton,
            inputStyle,
            placeholder,
            inputAttributes,
            recognitionPlaceholder,
            style,
            submitButtonStyle,
            width,
            height,
        } = this.props;
        const {patient}= this.state.store;
        const header = headerComponent || (
            <Header className="rsc-header">
                <HeaderTitle className="rsc-header-title">
                    {(patient.name) ? 'שלום, ' + patient.name : headerTitle }
                </HeaderTitle>
                {renderedSteps.length > 3 && (
                    <HeaderButton onClick={() => this.backOneStep()} className="rsc-header-backStep">
                        {prevStepButton}
                    </HeaderButton>
                )}
                {renderedSteps.length > 3 && (
                    <HeaderButton onClick={() => this.startAgain()} className="rsc-header-startAgain">
                        <span>{startAgainButton}</span>
                    </HeaderButton>
                )}
                {floating && (
                    <HeaderIcon className="rsc-header-close-button" onClick={() => this.toggleChatBot(false)}>
                        <CloseIcon/>
                    </HeaderIcon>
                )}
            </Header>
        );

        const icon = (this.isInputValueEmpty() || speaking) && recognitionEnable
            ? <MicIcon/> : <SubmitIcon/>;

        const inputPlaceholder = speaking
            ? recognitionPlaceholder
            : currentStep.placeholder || placeholder;

        const inputAttributesOverride = currentStep.inputAttributes || inputAttributes;

        return (
            <div className={`rsc ${className}`}>
                {floating && (
                    <FloatButton
                        id={'rsc-float-button'}
                        className="rsc-float-button"
                        style={floatingStyle}
                        opened={opened}
                        onClick={() => this.toggleChatBot(true)}
                    >
                        <ChatIcon/> <span style={{margin: '0px 5px'}}> {floatingText} </span>
                    </FloatButton>
                )}
                <ChatBotContainer
                    className="rsc-container"
                    floating={floating}
                    floatingStyle={floatingStyle}
                    opened={opened}
                    style={style}
                    width={width}
                    height={height}
                >
                    {!hideHeader && header}
                    <Content
                        className="rsc-content"
                        innerRef={contentRef => (this.content = contentRef)}
                        floating={floating}
                        style={contentStyle}
                        height={height}
                        hideInput={currentStep.hideInput}
                    >
                        {renderedSteps.map(this.renderStep)}
                    </Content>
                    <Footer className="rsc-footer" style={footerStyle}>
                        {
                            !currentStep.hideInput && (
                                <Input
                                    type="textarea"
                                    style={inputStyle}
                                    innerRef={inputRef => (this.input = inputRef)}
                                    className="rsc-input"
                                    placeholder={inputInvalid ? '' : inputPlaceholder}
                                    onKeyPress={this.handleKeyPress}
                                    onChange={this.onValueChange}
                                    value={inputValue}
                                    floating={floating}
                                    invalid={inputInvalid}
                                    disabled={disabled}
                                    hasButton={!hideSubmitButton}
                                    {...inputAttributesOverride}
                                />
                            )
                        }
                        {
                            !currentStep.hideInput && !hideSubmitButton && (
                                <SubmitButton
                                    className="rsc-submit-button"
                                    style={submitButtonStyle}
                                    onClick={this.handleSubmitButton}
                                    invalid={inputInvalid}
                                    disabled={disabled}
                                    speaking={speaking}
                                >
                                    {icon}
                                </SubmitButton>
                            )
                        }
                    </Footer>
                </ChatBotContainer>
            </div>
        );
    }
}

ChatBot.propTypes = {
    avatarStyle: PropTypes.objectOf(PropTypes.any),
    botAvatar: PropTypes.string,
    botDelay: PropTypes.number,
    bubbleOptionStyle: PropTypes.objectOf(PropTypes.any),
    bubbleStyle: PropTypes.objectOf(PropTypes.any),
    cache: PropTypes.bool,
    cacheName: PropTypes.string,
    className: PropTypes.string,
    contentStyle: PropTypes.objectOf(PropTypes.any),
    customDelay: PropTypes.number,
    customStyle: PropTypes.objectOf(PropTypes.any),
    enableMobileAutoFocus: PropTypes.bool,
    floating: PropTypes.bool,
    floatingIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    floatingStyle: PropTypes.objectOf(PropTypes.any),
    footerStyle: PropTypes.objectOf(PropTypes.any),
    handleEnd: PropTypes.func,
    headerComponent: PropTypes.element,
    headerTitle: PropTypes.string,
    startAgainButton: PropTypes.string,
    prevStepButton: PropTypes.string,
    height: PropTypes.string,
    hideBotAvatar: PropTypes.bool,
    hideHeader: PropTypes.bool,
    hideSubmitButton: PropTypes.bool,
    hideUserAvatar: PropTypes.bool,
    inputAttributes: PropTypes.objectOf(PropTypes.any),
    inputStyle: PropTypes.objectOf(PropTypes.any),
    opened: PropTypes.bool,
    toggleFloating: PropTypes.func,
    placeholder: PropTypes.string,
    recognitionEnable: PropTypes.bool,
    recognitionLang: PropTypes.string,
    recognitionPlaceholder: PropTypes.string,
    speechSynthesis: PropTypes.shape({
        enable: PropTypes.bool,
        lang: PropTypes.string,
        voice: PropTypes.instanceOf(window.SpeechSynthesisVoice),
    }),
    steps: PropTypes.arrayOf(PropTypes.object).isRequired,
    style: PropTypes.objectOf(PropTypes.any),
    submitButtonStyle: PropTypes.objectOf(PropTypes.any),
    userAvatar: PropTypes.string,
    userDelay: PropTypes.number,
    width: PropTypes.string,
};

ChatBot.defaultProps = {
    avatarStyle: {},
    botDelay: 1000,
    bubbleOptionStyle: {},
    bubbleStyle: {},
    cache: false,
    cacheName: 'rsc_cache',
    className: '',
    contentStyle: {},
    customStyle: {},
    customDelay: 1000,
    enableMobileAutoFocus: false,
    floating: false,
    floatingIcon: <ChatIcon/>,
    floatingStyle: {},
    footerStyle: {},
    handleEnd: undefined,
    headerComponent: undefined,
    headerTitle: 'Chat',
    startAgainButton: 'Start again',
    prevStepButton: 'Back',
    height: '520px',
    hideBotAvatar: false,
    hideHeader: false,
    hideSubmitButton: false,
    hideUserAvatar: false,
    inputStyle: {},
    opened: undefined,
    placeholder: 'Type the message ...',
    inputAttributes: {},
    recognitionEnable: false,
    recognitionLang: 'en',
    recognitionPlaceholder: 'Listening ...',
    speechSynthesis: {
        enable: false,
        lang: 'en',
        voice: null,
    },
    style: {},
    submitButtonStyle: {},
    toggleFloating: undefined,
    userDelay: 1000,
    width: '350px',
    botAvatar:
        'data:image/png;base64,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',
    userAvatar:
        'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiA/PjwhRE9DVFlQRSBzdmcgIFBVQkxJQyAnLS8vVzNDLy9EVEQgU1ZHIDEuMS8vRU4nICAnaHR0cDovL3d3dy53My5vcmcvR3JhcGhpY3MvU1ZHLzEuMS9EVEQvc3ZnMTEuZHRkJz48c3ZnIGVuYWJsZS1iYWNrZ3JvdW5kPSJuZXcgLTIwOC41IDIxIDEwMCAxMDAiIGlkPSJMYXllcl8xIiB2ZXJzaW9uPSIxLjEiIHZpZXdCb3g9Ii0yMDguNSAyMSAxMDAgMTAwIiB4bWw6c3BhY2U9InByZXNlcnZlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnNrZXRjaD0iaHR0cDovL3d3dy5ib2hlbWlhbmNvZGluZy5jb20vc2tldGNoL25zIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+PGc+PGNpcmNsZSBjeD0iLTE1OC41IiBjeT0iNzEiIGZpbGw9IiNGNUVFRTUiIGlkPSJNYXNrIiByPSI1MCIvPjxnPjxkZWZzPjxjaXJjbGUgY3g9Ii0xNTguNSIgY3k9IjcxIiBpZD0iTWFza18yXyIgcj0iNTAiLz48L2RlZnM+PGNsaXBQYXRoIGlkPSJNYXNrXzRfIj48dXNlIG92ZXJmbG93PSJ2aXNpYmxlIiB4bGluazpocmVmPSIjTWFza18yXyIvPjwvY2xpcFBhdGg+PHBhdGggY2xpcC1wYXRoPSJ1cmwoI01hc2tfNF8pIiBkPSJNLTEwOC41LDEyMXYtMTRjMCwwLTIxLjItNC45LTI4LTYuN2MtMi41LTAuNy03LTMuMy03LTEyICAgICBjMC0xLjcsMC02LjMsMC02LjNoLTE1aC0xNWMwLDAsMCw0LjYsMCw2LjNjMCw4LjctNC41LDExLjMtNywxMmMtNi44LDEuOS0yOC4xLDcuMy0yOC4xLDYuN3YxNGg1MC4xSC0xMDguNXoiIGZpbGw9IiNFNkMxOUMiIGlkPSJNYXNrXzNfIi8+PGcgY2xpcC1wYXRoPSJ1cmwoI01hc2tfNF8pIj48ZGVmcz48cGF0aCBkPSJNLTEwOC41LDEyMXYtMTRjMCwwLTIxLjItNC45LTI4LTYuN2MtMi41LTAuNy03LTMuMy03LTEyYzAtMS43LDAtNi4zLDAtNi4zaC0xNWgtMTVjMCwwLDAsNC42LDAsNi4zICAgICAgIGMwLDguNy00LjUsMTEuMy03LDEyYy02LjgsMS45LTI4LjEsNy4zLTI4LjEsNi43djE0aDUwLjFILTEwOC41eiIgaWQ9Ik1hc2tfMV8iLz48L2RlZnM+PGNsaXBQYXRoIGlkPSJNYXNrXzVfIj48dXNlIG92ZXJmbG93PSJ2aXNpYmxlIiB4bGluazpocmVmPSIjTWFza18xXyIvPjwvY2xpcFBhdGg+PHBhdGggY2xpcC1wYXRoPSJ1cmwoI01hc2tfNV8pIiBkPSJNLTE1OC41LDEwMC4xYzEyLjcsMCwyMy0xOC42LDIzLTM0LjQgICAgICBjMC0xNi4yLTEwLjMtMjQuNy0yMy0yNC43cy0yMyw4LjUtMjMsMjQuN0MtMTgxLjUsODEuNS0xNzEuMiwxMDAuMS0xNTguNSwxMDAuMXoiIGZpbGw9IiNENEIwOEMiIGlkPSJoZWFkLXNoYWRvdyIvPjwvZz48L2c+PHBhdGggZD0iTS0xNTguNSw5NmMxMi43LDAsMjMtMTYuMywyMy0zMWMwLTE1LjEtMTAuMy0yMy0yMy0yM3MtMjMsNy45LTIzLDIzICAgIEMtMTgxLjUsNzkuNy0xNzEuMiw5Ni0xNTguNSw5NnoiIGZpbGw9IiNGMkNFQTUiIGlkPSJoZWFkIi8+PC9nPjwvc3ZnPg==',
};

export default ChatBot;
